import { createFileRoute, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Dumbbell, Flame, HistoryIcon, User } from "lucide-react";
import { cn } from "@ds/lib/utils";
import { AppShell, POINTER_QUERY } from "@ds/patterns/app-shell";
import { TabBar } from "@ds/patterns/tab-bar";
import { RailNav } from "@/app/components/RailNav";
import { ScreenTransition } from "@/app/components/ScreenTransition";
import { SCREEN_INSET } from "@/app/lib/motion";
import { useMediaQuery } from "@ds/hooks/use-media-query";
import { Logo } from "@ds/ui/logo";
import { Skeleton } from "@ds/ui/skeleton";
import { env } from "@/app/config/env";
import { isFullBleedRoute } from "@/app/lib/full-bleed";
import { HeartRateProvider, HeartRateGround } from "@/shared/heart-rate";

/**
 * THE SIGNED-IN HALF OF THE APP.
 *
 * The guard turns on "anonymous" and NEVER on "unknown" — on launch the session token is
 * still being read out of the Keychain, and treating not-yet-known as signed-out bounces
 * every returning user to the sign-in screen for a frame. (The router is not mounted until
 * the session is known, so this branch is a second wall rather than the first.)
 *
 * The frame is the design system's shell: it owns the single scrolling region, the floating
 * navigation above the home indicator, and the safe-area insets. Screens pass content into
 * its slots and never position themselves.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: ({ context }) => {
    if (context.auth.status === "anonymous") throw redirect({ to: "/sign-in" });
  },
  component: AppLayout,
});

function AppLayout() {
  const { t } = useTranslation();
  const { auth } = Route.useRouteContext();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  /**
   * The shell turns the floating bar into a left rail above this width, but the bar itself
   * is shaped for a thumb and does not transform. Asked here, because the nav is built
   * before the shell mounts and so cannot read the shell's own layout context.
   */
  const isPointer = useMediaQuery(POINTER_QUERY);

  if (auth.status === "unknown") {
    return (
      <AppShell>
        {/* The frame hands over the full width now (8.3), so this supplies its own inset —
            the same one `ScreenTransition` gives every real screen. */}
        <div className={SCREEN_INSET}>
          <Skeleton className="h-8 w-40" />
          <Skeleton className="mt-7 h-32 w-full" />
        </div>
      </AppShell>
    );
  }

  /**
   * FOUR TABS, WHICH IS THE MAXIMUM `TabBar` TAKES — Today · Gym · History · You.
   *
   * WHICH ONE IS LIT IS DECIDED BY PREFIX, not by an exact path, because a tab stays lit
   * while you are on a screen pushed out of it. `/session/…` is pushed from Today, and
   * `/settings` is pushed from You (that is the only way in — see `_app.you.tsx`), so
   * neither of them may leave the bar with nothing selected. The fallthrough is Today
   * because `/` is Today.
   *
   * ⚠️ THE BAR STAYS UP ON PUSHED SCREENS — with ONE exception, taken deliberately below.
   * The design system's shell says navigation is "present on top-level screens and absent
   * inside flows", and running a session — strength, clock, debrief — is plainly a flow. The
   * reason it was left up everywhere is that hiding it means putting a way back in its place,
   * and for the strength and debrief screens that is still an open screen decision. The TIMER
   * already has one, which is what makes it decidable now rather than later — see `fullBleed`.
   */
  const TABS = [
    { id: "today", path: "/", label: t("nav.today"), icon: <Flame /> },
    { id: "gym", path: "/gym", label: t("nav.gym"), icon: <Dumbbell /> },
    { id: "history", path: "/history", label: t("nav.history"), icon: <HistoryIcon /> },
    { id: "you", path: "/you", label: t("nav.you"), icon: <User /> },
  ] as const;

  const current =
    pathname.startsWith("/gym")
      ? "gym"
      : pathname.startsWith("/history")
        ? "history"
        : pathname.startsWith("/you") || pathname.startsWith("/settings")
          ? "you"
          : "today";

  const navItems = TABS.map(({ id, label, icon }) => ({ id, label, icon }));

  /**
   * 🔑 THE ONE ROUTE THAT GETS THE WHOLE PIECE OF GLASS — the timer, and only the timer.
   *
   * WHY THIS SCREEN AND NOT THE OTHERS IN THE FLOW. The handoff draws the timer as a
   * full-screen surface with no navigation on it at all, and its colour IS the information:
   * green means work, rust means rest, read from two metres by someone who cannot focus. A
   * floating pill sitting in the middle of that, over the one control an exhausted athlete
   * has to find without looking, is chrome competing with the signal. The condition the other
   * pushed screens have not met is the one the shell's own rule turns on — a way back in its
   * place — and this screen has two: the ✕ in its header abandons the piece without logging,
   * and the footer control's swipe ends and logs it.
   *
   * ⚠️ WHAT THIS ACTUALLY CHANGES IN THE SHELL, so it is a decision and not a surprise. Two
   * things, both here rather than in `@ds/patterns/app-shell.tsx`, which is installed and
   * would lose a hand edit at the next install:
   *
   *   · `nav` is not passed, so the shell renders no floating bar and no rail.
   *   · the shell's main region holds its own bottom clearance for that bar
   *     (`pb-[calc(--spacing(28)+env(safe-area-inset-bottom))]`) unconditionally, so it is
   *     zeroed from the outside with a descendant variant. Without this the bar goes and its
   *     112px of empty app background stays — a dead strip under the ground, which is worse
   *     than the bar was. The screen supplies its own `pb-safe` in its place.
   *   · 🚨 AND ITS TOP PADDING (`pt-3`) GOES THE SAME WAY, for the same reason and against the
   *     same trap. The screen cancelled it with `-mt-3` for months and the cancellation never
   *     took: the scrolling stack inside is `overflow-x-hidden`, and a box with one axis hidden
   *     clips the other too — so the ground was laid out at 0 and painted from 12, leaving a
   *     black bar across the top of the phone and clipping the top four pixels of the
   *     heart-rate pill. Measured, not reasoned about. The padding has to be ABSENT, which is
   *     why the frame is told rather than fought. `__root.tsx` drops its safe-area inset on the
   *     same route for the same reason, off the same predicate.
   *
   * THE MATCH IS BY PATH and deliberately exact: `/session/<id>/timer` and nothing else in
   * the session flow, because nothing else in it has agreed to lose the bar yet.
   */
  const fullBleed = isFullBleedRoute(pathname);

  function go(id: string) {
    const tab = TABS.find((item) => item.id === id);
    if (tab) void navigate({ to: tab.path });
  }

  return (
    /**
     * 🚨 THE HEART-RATE CONNECTION IS HELD HERE, ABOVE EVERY SCREEN, AND IT HAS TO BE.
     *
     * A session is not one screen — it is the session screen, four block screens, the lift log,
     * the clock and the debrief, and each of those is a separate ROUTE that the router unmounts
     * when you leave it. The connection used to be owned by the clock screen's own hook, which
     * is why a strap could only be paired there and why heart rate ended when the metcon did.
     * Anything mounted lower than this loses the strap, the reconnect loop and the recorded
     * trace every time the athlete walks from the rack to the rig.
     *
     * ⚠️ MOUNTING IT ARMS NOTHING. It holds no connection until a session asks for one, and its
     * mount effect loads JavaScript without ever touching the radio — which is the difference
     * between this and an iOS Bluetooth prompt appearing because somebody opened the app.
     */
    <HeartRateProvider>
    <AppShell
      // See `fullBleed` above: the timer owns the whole screen, so the shell's bottom
      // clearance for a bar that is not rendered is taken back off its main region.
      // The crossfade is here for the same reason it is on the frame in `__root.tsx`: the
      // shell paints `bg-background`, which IS the heart-rate zone while a strap is live.
      className={cn(
        "motion-safe:transition-colors motion-safe:duration-[var(--ds-duration-slow)]",
        fullBleed ? "[&_[data-slot=app-shell-main]]:pt-0 [&_[data-slot=app-shell-main]]:pb-0" : undefined,
      )}
      /**
       * THE BRAND SLOT IS ALWAYS FILLED, and that is a rule rather than a nicety: the
       * system's logo falls back to the collection's own working name for it, and that name
       * must never appear in a product built on it. Passing this app's name is what keeps
       * someone else's codename off the screen.
       *
       * Phone screens show no brand mark at all (the shell only renders this on the rail),
       * so this is the pointer layout's mark.
       */
      brand={<Logo label={env.appName} interactive />}
      nav={
        fullBleed ? undefined : isPointer ? (
          <RailNav value={current} onValueChange={go} items={navItems} />
        ) : (
          <TabBar value={current} onValueChange={go} items={navItems} />
        )
      }
    >
      {/*
        THE TRANSITION LIVES HERE RATHER THAN AT THE ROOT, and the two are not
        interchangeable. Wrapping the root's outlet would put the shell itself — the tab bar,
        the brand, the safe-area frame — inside the thing that slides, so switching tabs
        would slide the tab bar off the screen along with the content. Here it wraps only
        what the shell renders as a screen, which is exactly the part that changes.

        The consequence, stated so nobody looks for a bug: crossing the auth boundary
        (sign-in ↔ the app) is still an instant cut. That change replaces the whole shell and
        is a redirect out of a route guard, not a navigation between two screens of a stack.
      */}
      <ScreenTransition>
        <Outlet />
      </ScreenTransition>
    </AppShell>

      {/*
        THE ZONE, EVERYWHERE — the athlete's current heart-rate zone as the app's own ground
        while a strap is live, at the same full strength and in the same colour the timer paints
        itself. `HeartRateGround` argues why that is a token swap rather than a layer.

        ⚠️ MOUNTED ON EVERY ROUTE, THE TIMER INCLUDED, AND IT USED TO BE GATED ON `fullBleed`.
        The gate existed because the old wash was a translucent LAYER, and laying zone four over
        a timer already painted zone four doubled it. A token swap cannot double anything: the
        clock paints `bg-hr-z4` from `TIMER_GROUNDS` and the ground underneath becomes the same
        `--ds-hr-z4`, which is one colour and not two. Skipping it here would instead mean the
        app's ground snapped back to black the moment the athlete opened the clock — visible
        the instant the timer's own surface is anything but opaque.

        It renders nothing, so its position in this tree is immaterial; it is here because this
        is the one component that knows a strap is connected.
      */}
      <HeartRateGround />
    </HeartRateProvider>
  );
}
